'use client';
import Script from 'next/script';
import Loading from '@/components/common/Loading';
import AppLogo from '@/components/common/AppLogo';
import { ProGloveDisplayTemplateId, useProGlove } from '@/components/proglove/ProgloveProvider';
import { apiSdkAtom } from '@/context/atoms/ApiSdk';
import { authenticationAtom } from '@/context/atoms/Authentication';
import { authMechanismAtom } from '@/context/atoms/AuthMechanism';
import { anyNodePermissions, userPermissionsAtom } from '@/context/atoms/UserPermissions';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import TextField from '@mui/material/TextField';
import { useAtom } from 'jotai';
import React, { useEffect, useRef, useState } from 'react';
import { transformAnyError } from '@/helper/errors';
import useUserFeedback from '@/helper/useUserFeedback';
import { WebEamAuthMechanism } from '@/helper/auth/WebEamAuthMechanism';
import { publicConfigAtom } from '@/context/atoms/publicConfig';

export const dynamic = 'force-dynamic';

export default function LogIn(): React.JSX.Element {
  const [auth] = useAtom(authenticationAtom);
  const [authMechanism] = useAtom(authMechanismAtom);
  const [api] = useAtom(apiSdkAtom);
  const [publicConfig] = useAtom(publicConfigAtom);
  const [, setAuthentication] = useAtom(authenticationAtom);
  const [, setAnyNodePermissions] = useAtom(userPermissionsAtom);
  const { enqueueFeedback } = useUserFeedback();
  const [refInput, setRefInput] = useState<HTMLInputElement | null>(null);
  const [submitDisabled, setSubmitDisabled] = useState<boolean>(false);
  const [showCredentialsFields, setShowCredentialsFields] = useState<boolean>(true);

  const proGloveDisplayUpdate = useRef<boolean>(false);
  const { updateDisplay } = useProGlove();

  useEffect(() => {
    if (!proGloveDisplayUpdate.current) {
      updateDisplay({
        display_template_id: ProGloveDisplayTemplateId.PG1,
        display_fields: [
          {
            display_field_id: 1,
            display_field_header: 'ekko',
            display_field_text: 'Please login to continue',
          },
        ],
      });
      proGloveDisplayUpdate.current = true;
    }

    return () => {
      proGloveDisplayUpdate.current = false;
    };
  }, [updateDisplay]);

  useEffect(() => {
    refInput?.focus();
  }, [refInput]);

  useEffect(() => {
    if (authMechanism instanceof WebEamAuthMechanism) {
      setShowCredentialsFields(false);
    }
  }, [publicConfig.env.AUTH_MECHANISM, authMechanism]);

  const authCallback = async (): Promise<void> => {
    await api.isAuthenticated();
    if (authMechanism.authentication.user?.permissions !== undefined) {
      setAnyNodePermissions(anyNodePermissions(authMechanism.authentication.user.permissions));
    }
    setAuthentication(authMechanism.authentication);
    setSubmitDisabled(false);
  };

  const loginApiCall = async (credentials: { username: string; password: string }): Promise<void> => {
    setSubmitDisabled(true);
    try {
      await api.authenticate(credentials, authCallback);
    } catch (e) {
      setSubmitDisabled(false);
      enqueueFeedback(transformAnyError(e), { variant: 'error' });
    }
  };

  const loginApiCallWebeam = async (): Promise<void> => {
    console.log('default: Running loginApiCallWebeam');

    setSubmitDisabled(true);

    try {
      await api.authenticate(undefined, authCallback);

      // Clean the URL hash after successful authentication
      if (window.location.hash.includes('access_token')) {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }
    } catch (e) {
      setSubmitDisabled(false);
      if (e instanceof Error) {
        enqueueFeedback(e.message, { variant: 'error' });
      }
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();

    if (authMechanism instanceof WebEamAuthMechanism) {
      void loginApiCallWebeam();
      return;
    }

    const data = new FormData(event.currentTarget);
    const username = data.get('username');
    const password = data.get('password');

    if (typeof username === 'string' && typeof password === 'string') {
      const credentials = {
        username,
        password,
      };
      void loginApiCall(credentials);
    }
  };

  if (auth.isAuthenticated) {
    return <Loading size={150} />;
  }

  return (
    <Container component="main" maxWidth="xs">
      {authMechanism instanceof WebEamAuthMechanism && (
        <Script
          src="auth/e2e.js"
          strategy="afterInteractive"
          onLoad={() => {
            if (!window.e2e) {
              throw new Error('E2E script not available');
            }
            const e2e: E2E = window.e2e;
            authMechanism.setE2E(e2e);

            // Auto-trigger authentication on page load when in WEBEAM mode
            loginApiCallWebeam();
          }}
        />
      )}
      <Box
        sx={{
          marginTop: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <AppLogo my={3} />
        <Box component="form" action={'dialog'} onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
          {showCredentialsFields && (
            <>
              <TextField
                inputRef={setRefInput}
                margin="normal"
                required
                fullWidth
                id="username"
                label="Username"
                name="username"
                autoComplete="username"
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type="password"
                id="password"
                autoComplete="current-password"
              />
            </>
          )}
          <Button disabled={submitDisabled} type="submit" fullWidth variant="contained" sx={{ mt: 3, mb: 2 }}>
            Sign In
          </Button>
        </Box>
      </Box>
    </Container>
  );
}
